/**
 * Modal 52-card grid for picking a single card. Grouped by suit (one row per
 * suit), ranks A→2. Cards already used elsewhere in the hand are disabled — the
 * caller passes `disabled` (typically `usedCards(hand)` minus the slot being
 * edited, so re-picking the current slot's own card stays allowed).
 */

import { createPortal } from "react-dom";

import { ModalOverlay } from "@/shared/components/ModalOverlay";
import { PlayingCard } from "@/features/chat/components/PlayingCard";

const RANKS = ["A", "K", "Q", "J", "T", "9", "8", "7", "6", "5", "4", "3", "2"];
const SUITS: { suit: string; label: string }[] = [
  { suit: "s", label: "Spades" },
  { suit: "h", label: "Hearts" },
  { suit: "d", label: "Diamonds" },
  { suit: "c", label: "Clubs" },
];

export function CardPicker({
  title = "Pick a card",
  disabled,
  onPick,
  onClose,
}: {
  title?: string;
  /** Tokens that can't be chosen (already placed elsewhere). */
  disabled: Set<string>;
  onPick: (token: string) => void;
  onClose: () => void;
}) {
  // Portal to <body>: the picker is opened from inside seat/board elements that
  // carry CSS transforms, and a transformed ancestor becomes the containing
  // block for `position: fixed` — which would trap this full-screen modal inside
  // a tiny seat box. Rendering on the body escapes that so it fills the viewport.
  return createPortal(
    <ModalOverlay title={title} onClose={onClose}>
      <div className="space-y-3">
        {SUITS.map(({ suit, label }) => (
          <div key={suit}>
            <div className="mb-1.5 text-xs font-medium text-text-muted">{label}</div>
            <div className="grid grid-cols-7 gap-1.5">
              {RANKS.map((rank) => {
                const token = `${rank}${suit}`;
                const isDisabled = disabled.has(token);
                return (
                  <button
                    key={token}
                    type="button"
                    disabled={isDisabled}
                    onClick={() => onPick(token)}
                    aria-label={`Pick ${token}`}
                    className={[
                      "flex items-center justify-center rounded-md p-0.5 transition",
                      isDisabled
                        ? "opacity-30 cursor-not-allowed"
                        : "hover:bg-accent/15 active:scale-95",
                    ].join(" ")}
                  >
                    <PlayingCard token={token} size="sm" />
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </ModalOverlay>,
    document.body,
  );
}
